import { CheckCircle2, Loader2, Settings, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CATEGORY_LABELS } from '@/lib/integrations-data';
import type { IntegrationConnection, IntegrationDefinition } from '@/types/integrations';

interface IntegrationCardProps {
  integration: IntegrationDefinition;
  connection?: IntegrationConnection;
  onConnect: (integration: IntegrationDefinition) => void;
  onConfigure: (integration: IntegrationDefinition, connection: IntegrationConnection) => void;
}

export function IntegrationCard({ integration, connection, onConnect, onConfigure }: IntegrationCardProps) {
  const isConnected = connection?.status === 'connected';
  const hasError = connection?.status === 'error';
  const isSyncing = connection?.status === 'syncing';

  const getStatusBadge = () => {
    if (isSyncing) {
      return (
        <Badge variant="outline" className="border-blue-500 text-blue-700 bg-blue-50">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Syncing
        </Badge>
      );
    }
    if (hasError) {
      return (
        <Badge variant="outline" className="border-red-500 text-red-700 bg-red-50">
          <XCircle className="h-3 w-3 mr-1" />
          Error
        </Badge>
      );
    }
    if (isConnected) {
      return (
        <Badge variant="outline" className="border-green-500 text-green-700 bg-green-50">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Connected
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-muted-foreground">
        Not Connected
      </Badge>
    );
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="text-4xl">
              {integration.logo.startsWith('http') ? (
                <img src={integration.logo} alt={`${integration.name} logo`} className="w-10 h-10 object-contain" />
              ) : (
                integration.logo
              )}
            </div>
            <div>
              <CardTitle className="text-lg">{integration.name}</CardTitle>
              <CardDescription className="text-xs mt-1">{CATEGORY_LABELS[integration.category]}</CardDescription>
            </div>
          </div>
          {isConnected && (
            <Button variant="ghost" size="icon" onClick={() => connection && onConfigure(integration, connection)}>
              <Settings className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{integration.description}</p>
          <div className="flex items-center justify-between">
            {getStatusBadge()}
            {isConnected ? (
              <Button variant="outline" size="sm" onClick={() => connection && onConfigure(integration, connection)}>
                Configure
              </Button>
            ) : (
              <Button size="sm" onClick={() => onConnect(integration)}>
                Connect
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
